/**
 * @file typedReveal.ts
 * @brief Word-by-word "typing" for the evidence bubble (DES 20-10-2026).
 *
 * @details
 * Words, not letters: Urdu letters join, so cutting inside a word would make
 * its shapes flicker as it grows. Each segment (plain text or the marked
 * quote) splits into the part shown so far and the part still to come; the
 * page draws the rest transparent so the bubble keeps its final size from the
 * first frame.
 *
 * @author DES
 * @date 20-10-2026
 */

export type TypedSegment = { text: string; marked: boolean };

/** How many words the segments hold (runs of non-space). */
export function countWords(segments: readonly TypedSegment[]): number {
  return segments.reduce((n, s) => n + (s.text.match(/\S+/g)?.length ?? 0), 0);
}

/** Each segment split at `shown` words in: what is typed, what is still to come. */
export function revealSegments(
  segments: readonly TypedSegment[],
  shown: number,
): { marked: boolean; visible: string; hidden: string }[] {
  let left = Math.max(0, shown);
  return segments.map(({ text, marked }) => {
    if (left <= 0) return { marked, visible: "", hidden: text };
    const words = /\S+/g;
    let cut = text.length;
    let seen = 0;
    let m: RegExpExecArray | null;
    while ((m = words.exec(text))) {
      seen += 1;
      if (seen === left) {
        cut = m.index + m[0].length;
        break;
      }
    }
    if (seen < left) {
      left -= seen;
      return { marked, visible: text, hidden: "" };
    }
    left = 0;
    return { marked, visible: text.slice(0, cut), hidden: text.slice(cut) };
  });
}

/** Milliseconds per word: about 1.4 s in all, never faster than 12 ms or slower than 55 ms. */
export function typingStepMs(words: number): number {
  if (words <= 0) return 0;
  return Math.min(55, Math.max(12, Math.round(1400 / words)));
}
